/**
 * Chart Replay Controls Component
 *
 * Allows users to replay historical price movements
 * Useful for strategy testing and market analysis
 */

import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, FastForward, X } from 'lucide-react';
import type { Time } from 'lightweight-charts';

interface ChartDataPoint {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface ChartReplayControlsProps {
  historicalData: ChartDataPoint[];
  onReplayData: (data: ChartDataPoint[]) => void;
  onClose: () => void;
}

const ChartReplayControls: React.FC<ChartReplayControlsProps> = ({
  historicalData,
  onReplayData,
  onClose,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [speed, setSpeed] = useState(1); // Speed multiplier
  const [showFromStart, setShowFromStart] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Base interval in milliseconds (1 second = 1 candle)
  const baseInterval = 1000;

  useEffect(() => {
    if (isPlaying && historicalData.length > 0) {
      const interval = baseInterval / speed;

      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= historicalData.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [isPlaying, speed, historicalData.length]);

  useEffect(() => {
    if (showFromStart) {
      // Show all data from start to current index
      const replayData = historicalData.slice(0, currentIndex + 1);
      onReplayData(replayData);
    } else {
      // Show only a sliding window (last 100 candles)
      const windowSize = 100;
      const start = Math.max(0, currentIndex - windowSize + 1);
      const replayData = historicalData.slice(start, currentIndex + 1);
      onReplayData(replayData);
    }
  }, [currentIndex, showFromStart, historicalData, onReplayData]);

  const handlePlayPause = () => {
    if (currentIndex >= historicalData.length - 1) {
      // If at the end, restart from beginning
      setCurrentIndex(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentIndex(0);
  };

  const handleSkipForward = () => {
    setCurrentIndex((prev) => Math.min(prev + 10, historicalData.length - 1));
  };

  const handleSkipBackward = () => {
    setCurrentIndex((prev) => Math.max(prev - 10, 0));
  };

  const formatTime = (index: number): string => {
    if (!historicalData[index]) return '--:--';
    const dataPoint = historicalData[index];
    const time = typeof dataPoint.time === 'number' ? dataPoint.time * 1000 : new Date(dataPoint.time as string).getTime();
    const date = new Date(time);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const progress = (currentIndex / (historicalData.length - 1)) * 100;

  return (
    <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 backdrop-blur-sm rounded-lg border border-purple-500/30 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FastForward size={16} className="text-purple-400 animate-pulse" />
          <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wide">
            Replay Mode
          </h3>
          <span className="text-xs text-purple-400/70 bg-purple-500/20 px-2 py-0.5 rounded">
            {formatTime(currentIndex)}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-purple-500/20 rounded transition-colors"
          title="Exit replay mode"
        >
          <X size={16} className="text-purple-300" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="relative w-full h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-200"
            style={{ width: `${progress}%` }}
          />
          <input
            type="range"
            min="0"
            max={historicalData.length - 1}
            value={currentIndex}
            onChange={(e) => setCurrentIndex(Number(e.target.value))}
            className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <div className="flex justify-between text-[10px] text-slate-500 mt-1">
          <span>{formatTime(0)}</span>
          <span>
            {currentIndex + 1} / {historicalData.length}
          </span>
          <span>{formatTime(historicalData.length - 1)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Skip Backward */}
          <button
            onClick={handleSkipBackward}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
            title="Skip backward 10 candles"
            disabled={currentIndex === 0}
          >
            <SkipBack size={14} className={currentIndex === 0 ? 'text-slate-500' : 'text-slate-200'} />
          </button>

          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="p-2 bg-purple-500 hover:bg-purple-600 rounded transition-colors"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause size={16} className="text-white" />
            ) : (
              <Play size={16} className="text-white" />
            )}
          </button>

          {/* Skip Forward */}
          <button
            onClick={handleSkipForward}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
            title="Skip forward 10 candles"
            disabled={currentIndex >= historicalData.length - 1}
          >
            <SkipForward
              size={14}
              className={currentIndex >= historicalData.length - 1 ? 'text-slate-500' : 'text-slate-200'}
            />
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs font-medium text-slate-200 transition-colors"
            title="Reset to start"
          >
            Reset
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Show from start toggle */}
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showFromStart}
              onChange={(e) => setShowFromStart(e.target.checked)}
              className="rounded"
            />
            <span>Full history</span>
          </label>

          {/* Speed Control */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Speed:</span>
            <select
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 focus:outline-none focus:border-purple-500"
            >
              <option value="0.5">0.5x</option>
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="5">5x</option>
              <option value="10">10x</option>
              <option value="20">20x</option>
            </select>
          </div>
        </div>
      </div>

      {/* Info Text */}
      <div className="mt-3 text-xs text-purple-300/70 bg-purple-500/10 px-3 py-2 rounded">
        {isPlaying ? (
          <span>Playing... Each candle represents ~{(1000 / speed).toFixed(0)}ms</span>
        ) : (
          <span>Paused - Use controls or drag the progress bar to navigate</span>
        )}
      </div>
    </div>
  );
};

export default ChartReplayControls;
